import { cfg } from "./marketplaceConfig";
import { feesOf } from "./fees";

/**
 * Time-weighted average of a step-function price series over `days`.
 * Each point holds until the next — a plain mean over-weights sample-dense days.
 * @param {[number, number][] | undefined} series  [unix_ts, value]
 * @param {number} days
 * @returns {number | null}
 */
export function timeWeightedAverage(series, days) {
  if (!series?.length) return null;
  const end = series[series.length - 1][0];
  const start = end - days * 86400;
  if (series[0][0] > end - 1) return null;
  let sum = 0;
  let span = 0;
  let prevT = start;
  let prevV = null;
  for (const [t, v] of series) {
    if (t <= start) {
      prevV = v;
      continue;
    }
    if (prevV != null) {
      const d = Math.min(t, end) - prevT;
      if (d > 0) {
        sum += prevV * d;
        span += d;
      }
    }
    prevT = Math.max(t, start);
    prevV = v;
    if (t >= end) break;
  }
  if (prevV != null && prevT < end) {
    sum += prevV * (end - prevT);
    span += end - prevT;
  }
  return span ? sum / span : null;
}

/**
 * Profit / ROI recomputed against pricing basis before min/max filters run.
 * Leads without history fall back to current Buy Box price (captured metrics).
 */
export function metricsFor(lead, filters, historyMap) {
  const basis = filters.basis || "Buy Box price";
  const days = basis === "Average 30 days" ? 30 : basis === "Average 90 days" ? 90 : 0;
  if (!days) {
    return { profit: lead.profit ?? 0, roi: lead.roiPct ?? 0, price: lead.destPrice };
  }
  const hist = historyMap?.[lead.id];
  const base = timeWeightedAverage(hist?.price, days);
  if (base == null) {
    return { profit: lead.profit ?? 0, roi: lead.roiPct ?? 0, price: lead.destPrice };
  }
  const fees = feesOf(lead);
  if (fees == null || lead.storePrice == null) {
    return { profit: lead.profit ?? 0, roi: lead.roiPct ?? 0, price: lead.destPrice };
  }
  const rate = cfg(lead.marketplace).referralFallbackPct;
  const fixed = Math.max(0, fees - lead.destPrice * rate);
  const pr = base - base * rate - fixed - lead.storePrice;
  return {
    profit: pr,
    roi: lead.storePrice ? (pr / lead.storePrice) * 100 : 0,
    price: base,
  };
}

export function relativeAge(iso) {
  if (!iso) return "—";
  const t = String(iso);
  if (!/^\d{4}-/.test(t)) return t; // already human string
  const diff = (Date.now() - new Date(t).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) {
    const n = Math.floor(diff / 60);
    return n <= 1 ? "a minute ago" : `${n} minutes ago`;
  }
  if (diff < 86400) {
    const n = Math.floor(diff / 3600);
    return n <= 1 ? "an hour ago" : `${n} hours ago`;
  }
  const n = Math.floor(diff / 86400);
  return n <= 1 ? "a day ago" : `${n} days ago`;
}

export function ageMinutes(isoOrLabel) {
  if (!isoOrLabel) return 1e9;
  if (/^\d{4}-/.test(String(isoOrLabel))) {
    return (Date.now() - new Date(isoOrLabel).getTime()) / 60000;
  }
  const t = String(isoOrLabel).toLowerCase();
  const n = /^(a|an)\b/.test(t) ? 1 : parseFloat(t) || 0;
  if (t.includes("second")) return n / 60;
  if (t.includes("minute")) return n;
  if (t.includes("hour")) return n * 60;
  if (t.includes("day")) return n * 1440;
  if (t.includes("month")) return n * 43200;
  return 1e9;
}
